import React, { useContext, useReducer, useEffect, useCallback } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PropTypes } from 'prop-types';
import styled from 'styled-components';
import StepWizard from '../../shared/StepWizard';
import PerformAction from '../../cardLibrary/PerformAction';
import Status from '../../cardLibrary/Status';
import GoBackRedirect from '../../shared/GoBackRedirect';
import CardTemplate from '../../shared/CardTemplate';
import { AppStateContext, AppDispatchContext } from '../../../context/AppContext';
import { VscCleanupContext, VscCleanupDispatchContext } from './context';
import { PortalContainer, StyledAlert, AccentButton } from '../../../styles/common';
import {
    paths,
    helpDeskPaths,
    PROGRESS_BAR_DONE_DELAY,
    MIN_VERSION_APP_VSC_DELETE,
} from '../../../constants';
import { executeDelete, ATTEMPT } from '../../../utils/vscCleanup';
import { reportCleanupOutcome } from '../../../utils/reportCleanupOutcome';
import { resolveTargetReader } from '../../../utils/resolveTargetReader';
import { identifyReader } from '../../../utils/identifyReader';
import { fetchDiagnosis } from '../../../utils/diagnosis';
import { errorFactory, declareError } from '../../../utils/errors';
import { compareVersionNumeric } from '../../../utils/generic';
import PCSC from '../../../utils/pcsc';

const CONFIRM = 'confirm';
const PERFORM = 'perform';
const STATUS = 'status';
const HOME = 'home';
// Delete is the only cleanup action (Repair was retired: it was Delete + a redirect to issuance).
const ACTION = 'DELETE';

const ButtonsRow = styled.div`
    display: flex;
    justify-content: flex-start;
    margin-top: 1rem;
    & > button {
        margin-left: 0.5rem;
    }
`;

// Terminal status message keys, keyed by the engine's attemptStatus vocabulary.
const messageForOutcome = (attemptStatus) => {
    if (attemptStatus === ATTEMPT.SUCCEEDED) return 'vscCleanup$success';
    if (attemptStatus === ATTEMPT.PARTIAL) return 'vscCleanup$partial';
    return 'vscCleanup$failure';
};

export function reducer(state, action) {
    switch (action.type) {
        case 'GO_NEXT':
            return { ...state, step: PERFORM, loading: true };
        case 'GO_BACK':
            return { ...state, step: HOME };
        case 'PROGRESS_BAR_DONE':
            return { ...state, isDone: true };
        case 'ATTEMPT_DONE':
            return {
                ...state,
                step: STATUS,
                loading: false,
                attemptStatus: action.payload.attemptStatus,
                // partialDetail/results carry substep-level detail for the audit
                // trail (PM-19637 Reading B: status lives on device state, history
                // in the audit log). This terminal Status screen intentionally does
                // not enumerate them -- not a bug.
                partialDetail: action.payload.partialDetail,
                results: action.payload.results,
                statusMessage: messageForOutcome(action.payload.attemptStatus),
                errorMessage: action.payload.attemptStatus !== ATTEMPT.SUCCEEDED,
            };
        // A dedicated terminal message for conditions that aren't an engine
        // outcome at all (e.g. the targeted VSC isn't on this endpoint), so
        // they don't get mislabeled with the generic failure copy.
        case 'TERMINAL_MESSAGE':
            return {
                ...state,
                step: STATUS,
                loading: false,
                attemptStatus: ATTEMPT.FAILED,
                partialDetail: null,
                results: [],
                statusMessage: action.payload.statusMessage,
                errorMessage: true,
            };
        default:
            return state;
    }
}

// Confirmation step. A plain component (not a bare div) so StepWizard's
// injected dispatch/state/title props don't leak onto a DOM node.
const ConfirmCleanup = ({ dispatch }) => {
    const { t } = useTranslation();
    return (
        <CardTemplate title={t('cardTitles$vscDelete')}>
            <PortalContainer>
                <StyledAlert color="warning">
                    {t('vscCleanup$confirmDelete')}
                </StyledAlert>
                <ButtonsRow>
                    <AccentButton outline color="primary" size="sm" onClick={() => dispatch({ type: 'GO_BACK' })}>
                        {t('flowButtonsElementLabels$cancel')}
                    </AccentButton>
                    <AccentButton
                        color="primary"
                        size="sm"
                        data-test-id="vsc-cleanup-confirm"
                        data-testid="vsc-cleanup-confirm"
                        onClick={() => dispatch({ type: 'GO_NEXT' })}
                    >
                        {t('flowButtonsElementLabels$next')}
                    </AccentButton>
                </ButtonsRow>
            </PortalContainer>
        </CardTemplate>
    );
};

ConfirmCleanup.propTypes = {
    dispatch: PropTypes.func,
};

// Default effect wiring; overridable via the `deps` prop for tests.
const defaultDeps = {
    resolveTargetReader: (device) =>
        resolveTargetReader(device, {
            listReaders: () => PCSC.list(),
            identify: identifyReader,
        }),
    fetchDiagnosis,
    executeDelete,
    reportCleanupOutcome,
    version: () => PCSC.version(),
};

const VscCleanup = ({ deps = defaultDeps }) => {
    const { device, goBackLink } = useContext(AppStateContext);
    const appDispatch = useContext(AppDispatchContext);
    const { pathname } = useLocation() || {};
    const { t } = useTranslation();
    const isHelpDesk = pathname ? pathname.includes(paths.helpDesk) : false;
    const pathsForMode = isHelpDesk ? helpDeskPaths : paths;
    const returnPath = goBackLink || pathsForMode.vscDelete;
    const [state, dispatch] = useReducer(reducer, {
        step: CONFIRM,
        loading: false,
        isDone: false,
        statusMessage: null,
        errorMessage: null,
    });

    const perform = useCallback(async () => {
        // Schedule a terminal reducer dispatch after the progress bar's fill
        // animation, same as before -- just factored out since it's now used
        // from more than one failure branch.
        const finishWith = (type, payload) => {
            dispatch({ type: 'PROGRESS_BAR_DONE' });
            setTimeout(() => dispatch({ type, payload }), PROGRESS_BAR_DONE_DELAY);
        };
        const finishGenericFailure = () =>
            finishWith('ATTEMPT_DONE', { attemptStatus: ATTEMPT.FAILED, partialDetail: null, results: [] });

        try {
            await deps.fetchDiagnosis(appDispatch);
        } catch (e) {
            // OS-Bridge unavailable (extension missing/disabled, unsupported
            // browser/OS, or too old) or a version mismatch: surface the
            // structured diagnosis UI instead of the generic terminal failure,
            // mirroring components/cardLibrary/LocalSettings/index.jsx. This
            // dispatches ADD_ERROR on the *app* context, which the route-level
            // ErrorBoundary (components/App/ComponentRoute.jsx) turns into a
            // WebpcscError render -- it takes over in place of this walkway's
            // own StepWizard, so no local reducer dispatch happens here.
            declareError(appDispatch, errorFactory.webpcsc((e && (e.marker || e.msg)) || 'diagnosis_failed', undefined, e));
            return;
        }

        // Backstop for deep links/stale rows: WebPCSC apps below 1.10.0 reject
        // `dvsc`, so stop before it is sent. Only the app version matters; an
        // unreadable version fails closed. Nothing was attempted, so nothing is reported.
        let ver;
        try {
            ver = await deps.version();
        } catch (e) {
            ver = null;
        }
        if (compareVersionNumeric(ver && ver.app, MIN_VERSION_APP_VSC_DELETE) < 0) {
            finishWith('TERMINAL_MESSAGE', { statusMessage: 'vscCleanup$bridgeUpdateRequired' });
            return;
        }

        let targetReader;
        try {
            targetReader = await deps.resolveTargetReader(device);
        } catch (e) {
            if (e && e.code === 'vsc:not_on_endpoint') {
                finishWith('TERMINAL_MESSAGE', { statusMessage: 'vscCleanup$notOnEndpoint' });
                return;
            }
            finishGenericFailure();
            return;
        }

        const ctx = {
            action: ACTION,
            initiatorType: 'USER',
            correlationId: null,
            requireAuthCode: false,
            targetReader,
            device,
            pinPolicy: {},
        };
        // A PCSC 'dvsc'/'cvsc' request carries a bridge-side timeout (see
        // sendRaw/timeout in utils/pcsc.js) that rejects with { code:
        // "TIMEOUT" } when the bridge never confirmed the native operation.
        // Unlike a definite failure (vsc:not_installed, vsc:unsupported_os),
        // the container may or may not have actually been mutated, so mark it
        // `.unknown` -- utils/vscCleanup.js's runStep() classifies that as
        // UNKNOWN -> PARTIAL rather than FAILED (which would incorrectly read
        // as "nothing happened, safe to retry"). An opaque HRESULT is left
        // classified FAILED (documented): we have no evidence it signals an
        // unconfirmed operation the way TIMEOUT does.
        const rejectUnknownOnTimeout = (err) => {
            if (err && err.code === 'TIMEOUT') {
                // eslint-disable-next-line no-param-reassign
                err.unknown = true;
            }
            throw err;
        };
        const engineDeps = {
            verifyAuthCode: async () => {},
            deleteVsc: (reader) => PCSC.deleteVirtualSmartCard(reader).catch(rejectUnknownOnTimeout),
        };

        let outcome;
        try {
            outcome = await deps.executeDelete(ctx, engineDeps);
        } catch (e) {
            finishGenericFailure();
            return;
        }

        // The engine result is authoritative. A report-transport failure
        // (timeout/network/403-with-message) must never downgrade a real
        // outcome -- e.g. a destructive op that actually SUCCEEDED must not be
        // shown to the user as a total failure, which would invite a
        // pointless retry. Reporting is therefore best-effort: it's
        // still awaited (so its completion is deterministic for tests/timing)
        // but a rejection here is only logged, never allowed to change
        // `outcome`.
        try {
            await deps.reportCleanupOutcome({ ctx, ...outcome, appDispatch });
        } catch (reportErr) {
            console.error('VscCleanup: reportCleanupOutcome failed (best-effort; shown outcome unaffected)', reportErr);
        }
        finishWith('ATTEMPT_DONE', outcome);
    }, [appDispatch, deps, device]);

    useEffect(() => {
        if (state.step === PERFORM) perform();
    }, [state.step, perform]);

    if (!device || !device.serial) {
        return <Navigate to={returnPath} replace />;
    }

    return (
        <VscCleanupContext.Provider value={state}>
            <VscCleanupDispatchContext.Provider value={dispatch}>
                <StepWizard
                    step={state.step}
                    title={t('cardTitles$vscDelete')}
                    dispatch={dispatch}
                    state={state}
                >
                    <GoBackRedirect path={returnPath} stepId={HOME} />
                    <ConfirmCleanup stepId={CONFIRM} />
                    <PerformAction stepId={PERFORM} isDone={state.isDone} />
                    <Status path={returnPath} stepId={STATUS} />
                </StepWizard>
            </VscCleanupDispatchContext.Provider>
        </VscCleanupContext.Provider>
    );
};

VscCleanup.propTypes = {
    deps: PropTypes.object,
};

export default VscCleanup;
